import * as React from 'react';
import { BudgetSections } from 'business/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import Typography from '@mui/material/Typography';
import { capitalize } from 'helpers/helpers';
import { translateBlockValue } from 'i18n';
import { styled } from '@mui/material/styles';
import {
    findBudgetProposalById,
    updateBudgetProposalDisplayPositionQueryData,
    updateBudgetProposalDisplayInSection,
} from './helper';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import SortableBudgetProposalItem from './SortableBudgetProposalItem';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useUpdateBudgetDisplaySettingsWithRollback } from './hooks/budgetDisplayHooks';
import { BudgetExerciceDisplaySettings } from './types';

type Props = { sortedBudgetProposals: BudgetProposalT[] };

const StyListWrapper = styled('div', { name: 'StyListWrapper' })({
    display: 'flex',
    flexFlow: 'column wrap',
    aligntems: 'flex-start',
});

const StySection = styled('div', { name: 'StySection' })({
    display: 'flex',
    flexFlow: 'column wrap',
    alignItems: 'flex-start',
    margin: '2rem 0 0 0',
});

const StyList = styled('div', { name: 'StyList' })({
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'flexStart',
});

const StyItemWrapper = styled('div', { name: 'StyItemWrapper' })({
    margin: '1rem 2rem 0 0',
});

const BudgetProposalList = ({
    sortedBudgetProposals,
}: Props): React.ReactElement => {
    const queryClient = useQueryClient();
    const sectionPanels = [
        null,
        BudgetSections.OPERATING,
        BudgetSections.INVESTMENT,
    ].filter((section) =>
        sortedBudgetProposals.some((bp) => bp.section === section),
    );

    const initalSectionBpIdsMap = useMemo(() => {
        return sortedBudgetProposals.reduce<Record<string, number[]>>(
            (acc, bp) => {
                const sectionKey = bp.section ?? 'null';
                acc[sectionKey] = acc[sectionKey] || [];
                acc[sectionKey].push(bp.id);
                return acc;
            },
            {},
        );
    }, [sortedBudgetProposals]);
    const [sectionBpIdsMap, setSectionBpIdsMap] = useState(
        initalSectionBpIdsMap,
    );

    useEffect(() => {
        if (
            JSON.stringify(sectionBpIdsMap) !==
            JSON.stringify(initalSectionBpIdsMap)
        ) {
            setSectionBpIdsMap(initalSectionBpIdsMap);
        }
    }, [initalSectionBpIdsMap]);

    const { mutate: updateBudgetDisplaySettings } =
        useUpdateBudgetDisplaySettingsWithRollback(setSectionBpIdsMap);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                // Enable sort function when dragging 10px otherwise disable event inside when it's just a click
                distance: 10,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    /**
     * When a drag operation ends, this function identifies the moved (active)
     * and destination (over) budget proposals, updates their order in the section items,
     * and syncs the new positions with the query data.
     */
    const handleDragEnd = useMemo(
        () =>
            ({ active, over }: DragEndEvent) => {
                if (!over || active.id === over.id) return;
                const movedBudgetProposal = findBudgetProposalById(
                    active.id as number,
                    sortedBudgetProposals,
                );
                const destinationBudgetProposal = findBudgetProposalById(
                    over.id as number,
                    sortedBudgetProposals,
                );
                if (!movedBudgetProposal || !destinationBudgetProposal) return;
                const updatedSectionItems =
                    updateBudgetProposalDisplayInSection(
                        sectionBpIdsMap,
                        movedBudgetProposal,
                        destinationBudgetProposal,
                        sortedBudgetProposals,
                    );
                const updatedBudgetSettings: BudgetExerciceDisplaySettings[] =
                    [];
                const uniqueBudget = new Set();

                for (const bp of sortedBudgetProposals) {
                    const newIndex = updatedSectionItems[
                        bp.section ?? 'null'
                    ].indexOf(bp.id);
                    if (newIndex === -1) continue;
                    if (!uniqueBudget.has(bp.budgetSoftwareId)) {
                        uniqueBudget.add(bp.budgetSoftwareId);
                        updatedBudgetSettings.push({
                            exercice: bp.exercice,
                            budgetSoftwareId: bp.budgetSoftwareId ?? '',
                            budgetDisplayPosition: newIndex,
                        });
                    }
                }

                setSectionBpIdsMap(updatedSectionItems);
                updateBudgetProposalDisplayPositionQueryData(
                    updatedSectionItems,
                    queryClient,
                );
                updateBudgetDisplaySettings({
                    body: updatedBudgetSettings,
                    prevValues: updatedSectionItems,
                });
            },
        [sectionBpIdsMap, sortedBudgetProposals],
    );

    return (
        <StyListWrapper>
            {sectionPanels.map((budgetSection) => {
                const filteredProposals = sortedBudgetProposals.filter(
                    (budget) => budget.section === budgetSection,
                );
                const header = budgetSection ? (
                    <Typography variant="body1Bold">
                        {capitalize(translateBlockValue(budgetSection, true))}
                    </Typography>
                ) : null;
                if (!sectionBpIdsMap[budgetSection ?? 'null']) return <></>;
                return (
                    <StySection
                        key={budgetSection}
                        data-testid={capitalize(
                            translateBlockValue(budgetSection ?? '', true),
                        )}
                    >
                        {header}
                        {/*Enables drag-and-drop functionality. It manages the drag events and applies collision detection logic.*/}
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            {/* Ensures that drag-and-drop sorting works correctly for the given list of items.
                             *  The 'items' prop defines the order of elements, for correct drag behavior.
                             */}
                            <SortableContext
                                items={sectionBpIdsMap[budgetSection ?? 'null']}
                            >
                                <StyList>
                                    {filteredProposals.map((budgetProposal) => (
                                        <StyItemWrapper key={budgetProposal.id}>
                                            <SortableBudgetProposalItem
                                                budgetProposal={budgetProposal}
                                            />
                                        </StyItemWrapper>
                                    ))}
                                </StyList>
                            </SortableContext>
                        </DndContext>
                    </StySection>
                );
            })}
        </StyListWrapper>
    );
};
export default BudgetProposalList;
